import { HttpClient } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { Observable, firstValueFrom } from "rxjs";
import { LanguageService } from "../language.service";
import translationsJson from "./translations.json";
import { environment } from "../../../environments/environment";

interface CrowdinManifest {
  languages: string[];
  custom_languages: { [key: string]: { locale: string } };
  content: { [key: string]: string[] };
  timestamp: number;
}

interface ProcessedTranslations {
  [key: string]: string;
}

interface CrowdinConfig {
  defaultLanguage?: string;
  fileFormat?: "json" | "crowdin-json";
}

interface CrowdinTranslationResponse {
  [key: string]: CrowdinTranslationItem;
}

interface CrowdinTranslationItem {
  context: string;
  identifier: string;
  labels: string;
  max_length: number | null;
  source_string: string;
  translation: string;
}

@Injectable({
  providedIn: "root",
})
export class CrowdinOtaService {
  private readonly currentLanguage = signal<string>("en");
  private readonly currentLanguage$ = toObservable(this.currentLanguage);
  private manifest: CrowdinManifest | null = null;
  private readonly translationsCache = new Map<string, ProcessedTranslations>();

  private readonly config: CrowdinConfig = {
    defaultLanguage: "en",
    fileFormat: "crowdin-json",
  };
  fallbackEnglishTranslations: any = null;

  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly _languageService: LanguageService
  ) {
    this.fallbackEnglishTranslations = translationsJson;
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLanguage$;
  }

  /**
   * Load manifest from Crowdin
   */
  public async loadManifest(): Promise<CrowdinManifest> {
    if (this.manifest) {
      return this.manifest;
    }

    try {
      const manifestUrl = `${environment.proxyEndpoint}/accessKeys/customer/manifest`;

      const manifest = await firstValueFrom(
        this.http.get<CrowdinManifest>(manifestUrl)
      );

      if (!manifest) {
        throw new Error("Failed to load Crowdin manifest");
      }

      this.manifest = manifest;
      return this.manifest;
    } catch (error) {
      console.error("Error loading Crowdin manifest:", error);
      throw error;
    }
  }

  /**
   * Get available languages from manifest
   */
  public async getAvailableLanguages(): Promise<string[]> {
    const manifest = await this.loadManifest();
    return manifest.languages;
  }

  /**
   * Check if language is supported
   */
  public async isLanguageSupported(languageCode: string): Promise<boolean> {
    const availableLanguages = await this.getAvailableLanguages();
    return availableLanguages.includes(languageCode);
  }

  /**
   * Get standards compliant locale for a language
   */
  public getStandardsCompliantLocale(
    languageCode: string,
    manifest: CrowdinManifest
  ): string {
    if (manifest?.languages.includes(languageCode)) {
      return manifest.content[languageCode][0];
    }
    return languageCode;
  }

  /**
   * Process Crowdin JSON format to simple key-value pairs
   */
  public processCrowdinTranslations(
    crowdinData: CrowdinTranslationResponse
  ): ProcessedTranslations {
    const processed: ProcessedTranslations = {};

    Object.values(crowdinData).forEach((item: CrowdinTranslationItem) => {
      // Use the identifier as the key and translation as the value
      processed[item.identifier] = item.translation;

      // Also support context-based keys if context is provided
      if (item?.context?.trim() !== "") {
        processed[item.context] = item.translation;
      }
    });

    return processed;
  }

  /**
   * Load translations for a specific language
   */
  async loadTranslations(languageCode: string): Promise<ProcessedTranslations> {
    // Check cache first
    const cacheKey = `${languageCode}-${this.config.fileFormat}`;
    if (this.translationsCache.has(cacheKey)) {
      return this.translationsCache.get(cacheKey)!;
    }

    try {
      const manifest = await this.loadManifest();

      if (!manifest.languages.includes(languageCode)) {
        throw new Error(`Language "${languageCode}" is not available`);
      }

      const locale = this.getStandardsCompliantLocale(languageCode, manifest);
      const url = `${environment.proxyEndpoint}/accessKeys/customer/translations?languageId=${locale}`;

      let rawTranslations: any = null;

      try {

        rawTranslations = await firstValueFrom(this.http.get(url));
      } catch (error) {
        console.warn(`Failed to load from ${url}:`, error);
        this.handleDefaultTranslationsLoad();
        throw error;
      }

      let processedTranslations: ProcessedTranslations;

      if (this.config.fileFormat === "crowdin-json") {
        // Handle Crowdin's format with metadata
        processedTranslations = this.processCrowdinTranslations(
          rawTranslations as CrowdinTranslationResponse
        );
      } else {
        // Handle simple key-value JSON format
        processedTranslations = rawTranslations as ProcessedTranslations;
      }

      // Cache the processed translations
      this.translationsCache.set(cacheKey, processedTranslations);

      return processedTranslations;
    } catch (error) {
      console.error(`Error loading translations for ${languageCode}:`, error);
      this.handleDefaultTranslationsLoad();
      throw error;
    }
  }

  /**
   * Switch to a specific language
   */
  async useLanguage(languageCode: string): Promise<void> {
    try {
      const isSupported = await this.isLanguageSupported(languageCode);

      if (!isSupported) {
        console.warn(
          `Language "${languageCode}" not supported. Using default language.`
        );
        languageCode = this.config.defaultLanguage ?? "en";
      }

      const translations = await this.loadTranslations(languageCode);

      this.translate.setTranslation(languageCode, translations, true);
      this.translate.use(languageCode);

      this.currentLanguage.set(languageCode);
      this._languageService.setIsLanguageSelectionOpen(false);


    } catch (error) {
      console.error("Error switching language:", error);
      this.handleDefaultTranslationsLoad();
    }
  }

  /**
   * Initialize with stored language preference
   */
  public async initializeLanguage(): Promise<void> {
    this.handleDefaultTranslationsLoad();

    let storedLanguage = this._languageService.getLanguage();
    storedLanguage = storedLanguage.split("-")[0];
    const defaultLanguage =
      storedLanguage ?? this.config.defaultLanguage ?? "en";

    await this.useLanguage(defaultLanguage);
  }

  public handleDefaultTranslationsLoad() {
    const processed = this.processCrowdinTranslations(this.fallbackEnglishTranslations);
    this.translate.setTranslation("en", processed, false);
    this.translate.use("en");
  }
}
